import React, { useState, useEffect } from 'react';
import Spinner from '../Spinner/Spinner';
import { getContributionReportCsvUrl } from '../../utils/publicData';
import { splitCsvLine } from '../../utils/csv';

/*
PURPOSE: Editorial card displaying ecosystem vitality metrics for an artist —
total pulses, monthly + total support, stage, 0–100 health gauge, engagement
indicator, and a per-stage advice line.

USE CASES:
- Rendered inside the PublicArtist hero (left column) at /a/:id.
- Rendered inside the ArtistPayoutCollection hero (right column) at
  /a/:artistUrl/collect, stacked with ArtistPayoutChecker + the verification
  form card.

DOMAIN BOUNDARIES:
- Responsible for fetching and displaying ecosystem data for a specific
  artist. Read-only visualisation; no user interactions beyond display.
- Visual treatment is editorial (white card chrome, Cormorant numbers,
  editorial palette). Semantic green/red engagement dot preserved for
  a11y. Stage/health derivation + advice text are unchanged from the
  legacy implementation; only the render path is restyled.

ASSUMPTIONS:
- Artist name uniquely identifies an ecosystem.
- Transaction data is available in CSV format from S3.
- Most recent month of activity is the key measurement period.
*/

interface EcosystemVitalsProps {
  artistName: string;
}

const EcosystemVitals: React.FC<EcosystemVitalsProps> = ({ artistName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // We'll store raw data plus derived stage & health
  const [stats, setStats] = useState({
    totalPulses: 0,
    monthlySupport: 0,
    totalSupport: 0,
    ecosystemStage: '',
    ecosystemHealth: 0, // We'll treat this as a 0–100 numeric gauge
    ecosystemHealthLabel: '', // e.g., "Healthy" / "Unhealthy"
    ecosystemAdvice: '',
    hasReceivedPayment: false // New field to track if artist has received any payments
  });

  // --------------------------------------
  // Utility functions for Stage & Health
  // --------------------------------------

  // 1) Determine the ecosystem stage
  const getEcosystemStage = (
    pulsesInLastMonth: number,
    monthlySupport: number,
    totalPulses: number,
    hasReceivedPayment: boolean
  ): string => {
    if (totalPulses === 0) {
      return 'Vision';
    }
    
    // If artist has received payment, they can reach any stage based on metrics
    if (hasReceivedPayment) {
      // Follow updated rules in order:
      //  - Resilient: monthlySupport > 150 && totalPulses > 10
      //  - Thriving: monthlySupport > 50
      //  - Coordinating: hasReceivedPayment is true (minimum level)
      if (monthlySupport > 150 && totalPulses > 10) {
        return 'Resilient';
      } else if (monthlySupport > 50) {
        return 'Thriving';
      } else {
        return 'Coordinating'; // Minimum level for artists who have received payment
      }
    } else {
      // When no payments have been received, maximum level is Coordinating
      if (pulsesInLastMonth > 1) {
        return 'Coordinating'; // Maximum level without payment
      } else if (pulsesInLastMonth === 1) {
        return 'Emerging';
      } else {
        return 'Dormant';
      }
    }
  };

  // 2) Determine if earliest pulse is less than 6 months old
  const isEarliestPulseLessThan6Months = (earliestDateStr: string): boolean => {
    if (!earliestDateStr) return false; // fallback if no date found

    // Format: YYYYMMDD
    const year = parseInt(earliestDateStr.substring(0, 4));
    const month = parseInt(earliestDateStr.substring(4, 6)) - 1; // zero-based
    const day = parseInt(earliestDateStr.substring(6, 8));

    const earliestDate = new Date(year, month, day);
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    return earliestDate >= sixMonthsAgo;
  };

  // 3) Determine textual "health" from stage & earliest date
  const getEcosystemHealthLabel = (
    stage: string,
    earliestDateStr: string,
    totalPulses: number,
    pulsesInLastMonth: number,
    hasReceivedPayment: boolean
  ): 'Healthy' | 'Unhealthy' | 'Neutral' => {
    // If artist has received payment, ecosystem is generally considered healthier
    if (hasReceivedPayment) {
      if (['Coordinating', 'Thriving', 'Resilient'].includes(stage)) {
        return 'Healthy';
      }
    }

    if (stage === 'Emerging') {
      // If earliest pulse < 6 months ago => Healthy
      if (isEarliestPulseLessThan6Months(earliestDateStr)) {
        return 'Healthy';
      } else {
        // If earliest pulse is older than 6 months & there's only 1 pulse in the last month => Unhealthy
        if (pulsesInLastMonth === 1) {
          return 'Unhealthy';
        }
      }
    }
    // You can define additional logic for other stages, or default to 'Neutral'
    return 'Neutral';
  };

  // 4) Map stage & health label to a 0–100 gauge
  const getEcosystemHealthGauge = (
    stage: string,
    healthLabel: string,
    hasReceivedPayment: boolean
  ): number => {
    // Modify the gauge to reflect payment status
    const paymentBonus = hasReceivedPayment ? 10 : 0;
    
    switch (stage) {
      case 'Vision':
        return 5;
      case 'Resilient':
        return 95 + paymentBonus > 100 ? 100 : 95 + paymentBonus;
      case 'Thriving':
        return 80 + paymentBonus;
      case 'Coordinating':
        return 65 + paymentBonus;
      case 'Emerging':
        if (healthLabel === 'Healthy') return 50 + paymentBonus;
        if (healthLabel === 'Unhealthy') return 25 + paymentBonus;
        return 35 + paymentBonus; // Some mid-point for "Neutral Emerging"
      case 'Dormant':
        return 15 + paymentBonus;
      default:
        return 10 + paymentBonus;
    }
  };

  const getEcosystemAdvice = (stage: string, healthLabel: string, hasReceivedPayment: boolean, artistName?: string): string => {
    // Include payment collection information in advice that emphasizes the reciprocal relationship
      
    if (stage === 'Vision') {
      return (
        `${artistName ? artistName + "'s" : "This"} ecosystem is waiting for its first coral. ` +
        "Your backing shows others that collective support is real—" +
        "making it easier for them to join in."
      );
    }
    if (stage === 'Emerging') {
        if (healthLabel === 'Healthy') {
        return (
            "Initial support patterns are establishing a foundation for the ecosystem. " +
            "Each pulse signals appreciation and gratitude for the artist's work. "
        );
        } else if (healthLabel === 'Unhealthy') {
        return (
            "Early support shows potential for meaningful connection. " +
            "Additional backers will strengthen the ecosystem. "
        );
        } else {
        return (
            "Recent pulse activity has opened channels of support. " +
            "Regular contributions help establish sustainable cooperation " +
            "between artist and community. "
        );
        }
    } else if (stage === 'Coordinating') {
        if (hasReceivedPayment) {
          return (
          "Corals are pulsing in coordination with the artist, creating a rhythm of exchange. " +
          "This pattern of support demonstrates growing mutual engagement. "
          );
        } else {
          return (
          "Corals are growing together, creating waves of support. " +
          "Artist engagement is required to sustain the ecosystem. "
          );
        }
    } else if (stage === 'Thriving') {
        return (
        "The exchange between community and artist is flourishing. " +
        "Regular pulses have created a diverse and strong foundation for " +
        "ongoing creative dialogue. "
        );
    } else if (stage === 'Resilient') {
        return (
        "A vibrant conversation of support flows through established trust paths. " +
        "The artist and community are engaged in a sustainable exchange " +
        "that nurtures creativity and connection. "
        );
    } else if (stage === 'Dormant') {
        return (
        "This ecosystem's conversation has temporarily paused. " +
        "New pulses will reactivate the dialogue between artist and supporters, " +
        "renewing the cycle of exchange. "
        );
    }
    return '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(getContributionReportCsvUrl());
        const text = await response.text();
        const rows = text.split('\n').slice(1); // Skip header

        // We'll count every row for the correct artist as a pulse
        let allPulsesCount = 0;
        let earliest = '99999999';
        let monthlyTotal = 0;
        let allTimeTotal = 0;
        let hasReceivedPayment = false;

        // Define interface for artist transactions
        interface ArtistTransaction {
          date: string;
          formattedDate: string;
          amount: number;
          isInLastMonth: boolean;
          paymentDistributed: string;
        }

        // For debugging - collect all transactions for this artist
        const artistTransactions: ArtistTransaction[] = [];

        // For "pulses in last month"
        let pulsesInLastMonth = 0;
        
        // Find the most recent date FOR THIS SPECIFIC ARTIST (not the entire CSV)
        let artistMostRecentDate = '00000000';

        rows.forEach((row) => {
          if (!row.trim()) return;
          const [dateStr, , , name] = splitCsvLine(row);
          if (name?.trim().toLowerCase() === artistName.toLowerCase()) {
            if (dateStr > artistMostRecentDate) {
              artistMostRecentDate = dateStr;
            }
          }
        });

        // Calculate "last month" window from TODAY, not the artist's most recent transaction.
        // This ensures inactive artists show $0 monthly, not their last active month's amount.
        const today = new Date();
        const todayStr = `${today.getFullYear()}${(today.getMonth() + 1).toString().padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}`;
        const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        const oneMonthBeforeDateStr =
          `${oneMonthAgo.getFullYear()}${(oneMonthAgo.getMonth() + 1).toString().padStart(2, '0')}${oneMonthAgo.getDate().toString().padStart(2, '0')}`;

        // Process transactions
        rows.forEach((row) => {
          if (!row.trim()) return;
          const [dateStr, , amount, name, , , paymentDistributed] = splitCsvLine(row);

          if (name?.trim().toLowerCase() === artistName.toLowerCase()) {
            allPulsesCount += 1;

            if (dateStr < earliest) {
              earliest = dateStr;
            }

            const floatAmount = parseFloat(amount || '0');
            allTimeTotal += floatAmount;

            // Check if this artist has received any payments
            if (paymentDistributed && paymentDistributed.trim().toLowerCase() === 'yes') {
              hasReceivedPayment = true;
            }

            // Use a strict "after" comparison to make the range exclusive at the start
            // For Feb 28 -> Jan 29 (not including Jan 28)
            const isInLastMonth = dateStr > oneMonthBeforeDateStr &&
                                 dateStr <= todayStr;
            
            // For debugging - also create Date objects to see the difference
            const year = parseInt(dateStr.substring(0, 4));
            const month = parseInt(dateStr.substring(4, 6)) - 1;
            const day = parseInt(dateStr.substring(6, 8));
            const transactionDate = new Date(year, month, day);
            
            // Collect for debugging
            artistTransactions.push({
              date: dateStr,
              formattedDate: transactionDate.toISOString(),
              amount: floatAmount,
              isInLastMonth,
              paymentDistributed: paymentDistributed || 'unknown'
            });

            if (isInLastMonth) {
              monthlyTotal += floatAmount;
              pulsesInLastMonth += 1;
            }
          }
        });

        // Derive Stage - now with payment status parameter
        const stageLabel = getEcosystemStage(
          pulsesInLastMonth,
          monthlyTotal,
          allPulsesCount,
          hasReceivedPayment
        );

        // Derive Health Label - now with payment status parameter
        const healthLabel = getEcosystemHealthLabel(
          stageLabel,
          earliest,
          allPulsesCount,
          pulsesInLastMonth,
          hasReceivedPayment
        );

        // Derive a numeric gauge 0–100 - now with payment status parameter
        const healthGauge = getEcosystemHealthGauge(stageLabel, healthLabel, hasReceivedPayment);

        // Advice text - now with payment status parameter
        const adviceText = getEcosystemAdvice(stageLabel, healthLabel, hasReceivedPayment, artistName);

        setStats({
          totalPulses: allPulsesCount,
          monthlySupport: Math.floor(monthlyTotal),
          totalSupport: Math.floor(allTimeTotal),
          ecosystemStage: stageLabel,
          ecosystemHealth: healthGauge,
          ecosystemHealthLabel: healthLabel,
          ecosystemAdvice: adviceText,
          hasReceivedPayment
        });

        setLoading(false);
      } catch (err) {
        setError('Failed to load ecosystem data');
        setLoading(false);
      }
    };

    if (artistName) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistName]);

  // Editorial card chrome — matches EditorialImpactCalculator / EditorialCreatorCard.
  const cardChrome =
    'bg-white rounded-3xl border border-editorial-muted/10 shadow-[0_24px_48px_-24px_rgba(12,19,45,0.12)] p-7 md:p-9';
  // In-card eyebrow styling (the section-level <Eyebrow> primitive carries a
  // hairline rule above it that doesn't fit inside a card).
  const eyebrowInline =
    'text-[11px] tracking-[0.18em] uppercase text-editorial-muted/55';

  if (loading) {
    return (
      <div className={cardChrome}>
        <div className="flex justify-center items-center h-32">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cardChrome}>
        <p className="text-editorial-muted/80 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className={cardChrome}>
      <h4 className={eyebrowInline}>Ecosystem Vitals</h4>

      <div className="mt-6 flex flex-col sm:flex-row sm:gap-8 sm:items-start">
        {/* Stats Row — wraps on mobile */}
        <div className="flex gap-6 sm:gap-8 mb-5 sm:mb-0">
          {/* Total Pulses */}
          <div>
            <p className="font-cormorant text-editorial-muted text-3xl md:text-4xl tabular-nums leading-none">
              {stats.totalPulses}
            </p>
            <p className={`${eyebrowInline} mt-2`}>Pulses</p>
          </div>

          {/* Monthly Support */}
          <div>
            <p className="font-cormorant text-editorial-muted text-3xl md:text-4xl tabular-nums leading-none">
              <span className="text-xl md:text-2xl text-editorial-muted/70">$</span>
              {stats.monthlySupport}
            </p>
            <p className={`${eyebrowInline} mt-2`}>Monthly</p>
          </div>

          {/* Total Support */}
          <div>
            <p className="font-cormorant text-editorial-muted text-3xl md:text-4xl tabular-nums leading-none">
              <span className="text-xl md:text-2xl text-editorial-muted/70">$</span>
              {stats.totalSupport}
            </p>
            <p className={`${eyebrowInline} mt-2`}>Total</p>
          </div>
        </div>

        {/* Stage & Health Gauge — moves to new line on mobile */}
        <div className="sm:border-l sm:border-editorial-muted/10 sm:pl-8 border-t sm:border-t-0 border-editorial-muted/10 pt-5 sm:pt-0">
          <p className={eyebrowInline}>Stage</p>
          <p className="mt-1 font-cormorant text-editorial-muted text-2xl md:text-3xl leading-none">
            {stats.ecosystemStage}
          </p>
          <div className="mt-3 w-24 bg-editorial-muted/10 rounded-full h-2">
            <div
              className="bg-coral-orange h-2 rounded-full transition-all"
              style={{ width: `${stats.ecosystemHealth}%` }}
            ></div>
          </div>
          <p className="mt-2 text-editorial-muted/70 text-sm">
            Health: {stats.ecosystemHealthLabel}
          </p>
        </div>
      </div>

      {/* Artist Engagement indicator — semantic green/red preserved for a11y */}
      {stats.ecosystemStage !== 'Vision' && (
        <div className="mt-6 flex items-center gap-2 pt-5 border-t border-editorial-muted/10">
          <span className="text-sm font-medium text-editorial-muted">Artist Engagement:</span>
          <span
            className={`w-2.5 h-2.5 rounded-full ${stats.hasReceivedPayment ? 'bg-emerald-600' : 'bg-red-700'}`}
            aria-hidden="true"
          ></span>
          <span className="text-editorial-muted/80 text-sm">
            {stats.hasReceivedPayment ? 'Actively receiving contributions' : 'Yet to collect contributions'}
          </span>
        </div>
      )}

      {/* Per-stage advice text */}
      {stats.ecosystemAdvice && (
        <p className="mt-5 text-editorial-muted/80 text-sm leading-[1.6]">
          {stats.ecosystemAdvice}
        </p>
      )}
    </div>
  );
};

export default EcosystemVitals;